import './lobby.css';
import { t, tText, onLang } from './i18n';
import { THEMES, type ThemeId, type VariantId } from '../config';
import { ENV_IDS, DEFAULT_ENV, type EnvId } from '../render/Environments';
import { AVATAR_ENABLED, paintAvatar, parseAvatar, type AvatarConfig } from '../avatar';
import { tierLabel, tierStepsApart } from './tier';
import { FORMAT_INFO, defaultFinish, type AuthProvider, type BreakStyle, type FinishRule, type GameFormat, type LeaderboardEntry, type LobbyState, type RoomInfo } from '../net/protocol';

/**
 * Online lobby overlay with three screens:
 *  - browser: the list of open rooms + a "create room" button (login is deferred).
 *  - create:  choose the game (4-ball / 3-cushion) and format (1v1/2v2/1v1v1/1v1v1v1).
 *  - waiting room: team slots with per-player "ready" and the start gate.
 * App owns the socket; the Lobby only renders state and reports intent via callbacks.
 * Closing the browser returns to the main menu (App wires onClose).
 */
export interface LobbyCallbacks {
  onNickname(name: string): void;
  onReroll(): void;
  onRefresh(): void;
  onCreate(name: string, format: GameFormat, variant: VariantId, finish: FinishRule, breakStyle: BreakStyle, stake: number): void;
  onJoin(code: string): void;
  onLeave(): void;
  onReady(ready: boolean): void;
  onStart(): void;
  /** Opens the item store/equip modal from the waiting room, so purchased
   *  items (cue-ball colour, etc.) can be equipped before the match starts. */
  onOpenItems(): void;
  onKick(id: string): void;
  onTheme(theme: ThemeId): void;
  onEnv(env: EnvId): void;
  onClose(): void;
  onEditAvatar(): void;
  onLogin(name: string, password: string): void;
  onSignup(name: string, password: string, email: string): void;
  onSocialLogin(provider: string): void;
  onLogout(): void;
}

const FORMATS: GameFormat[] = ['1v1', '2v2', '1v1v1', '1v1v1v1'];
/** 코인 stake presets the host can pick (0 = no bet); mirrors server STAKE_OPTIONS. */
const STAKES = [0, 100, 500, 1000, 2000, 3000, 5000];
const VARIANTS: VariantId[] = ['straight-rail', 'three-cushion'];
/** Mirrors server MAX_STAKE_RANK_GAP: how far a joiner's tier may sit from
 *  the host's before a staked room refuses the seat. */
const MAX_STAKE_RANK_GAP = 3;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/** Which board the room browser is showing. */
type Tab = 'rooms' | 'rank';

export class Lobby {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly cb: LobbyCallbacks;

  // browser screen
  private readonly browser = el('div', 'lobby-screen');
  private readonly avatarThumb = el('button', 'avatar-thumb');
  private readonly nick = el('input', 'lobby-nick') as HTMLInputElement;
  private readonly reroll = el('button', 'lobby-reroll');
  private readonly roomList = el('div', 'lobby-rooms');
  private rooms: RoomInfo[] = [];
  private readonly status = el('div', 'lobby-status');
  // the browser's two views live behind tabs so the panel is one screen, not an
  // endless column: the room list (the reason you're here) and the ladder
  private readonly roomsPane = el('div', 'lobby-pane');
  private readonly rankPane = el('div', 'lobby-pane');
  private readonly tabBtns: Record<Tab, HTMLButtonElement> = {} as never;
  private tab: Tab = 'rooms';
  // account auth (optional; anonymous play needs none of it)
  private readonly authBox = el('div', 'lobby-auth');
  private readonly authUser = el('input', 'lobby-authinput') as HTMLInputElement;
  private readonly authPass = el('input', 'lobby-authinput') as HTMLInputElement;
  private readonly authEmail = el('input', 'lobby-authinput') as HTMLInputElement;
  private readonly authMsg = el('div', 'lobby-authmsg');
  private account: string | null = null;
  private accountId: string | null = null;
  private isGuest = true;
  /** Force the guest auth disclosure open (main-menu 로그인/가입 buttons land here already expanded). */
  private authForceOpen = false;
  /** Social providers the server can complete a login with (empty = none configured). */
  private providers: AuthProvider[] = [];
  // top-players leaderboard
  private readonly board = el('div', 'lobby-board');

  // create screen
  private readonly create = el('div', 'lobby-screen');
  private readonly varChoices = el('div', 'lobby-choices');
  private readonly fmtChoices = el('div', 'lobby-choices');
  private readonly caromChoices = el('div', 'lobby-choices');
  private readonly bankChoices = el('div', 'lobby-choices');
  private readonly finishBox = el('div', 'lobby-finish');
  private chosenVariant: VariantId = 'straight-rail';
  private chosenFormat: GameFormat = '1v1';
  /** Finish rule (4-ball 마지막 3쿠션), chosen at creation time and locked in for the room. */
  private chosenFinish: FinishRule = defaultFinish('1v1');
  /** Opening style (4-ball only) and 코인 stake, also chosen at creation and locked in. */
  private readonly breakRow = el('div', 'lobby-row');
  private readonly breakBtns: HTMLButtonElement[] = [];
  private chosenBreakStyle: BreakStyle = 'preset';
  private readonly stakeRow = el('div', 'lobby-row');
  private readonly stakeSel = el('select', 'lobby-fmt') as HTMLSelectElement;
  private readonly stakeHint = el('div', 'lobby-sublbl lobby-stakehint');
  private chosenStake = 0;
  /** Live 코인 balance (App.syncCoins): gates both the stake picker below and
   *  the Join buttons in the room list. */
  private coins = 0;
  /** This account's ladder tier (null before a profile has loaded): gates
   *  joining a staked room whose host is too far up/down the ladder. */
  private myTier: number | null = null;
  /** Table colour + background scene: also chosen at creation, applied immediately
   *  to the local (practice) table so the picker doubles as a live preview. */
  private readonly themeRow = el('div', 'lobby-themerow');
  private readonly themeBtns: HTMLButtonElement[] = [];
  private chosenTheme = Object.keys(THEMES)[0] as ThemeId;
  private readonly envRow = el('div', 'lobby-themerow');
  private readonly envSel = el('select', 'lobby-fmt') as HTMLSelectElement;
  private chosenEnv: EnvId = DEFAULT_ENV;

  // waiting-room screen
  private readonly wait = el('div', 'lobby-screen');
  private readonly waitTitle = el('div', 'lobby-roomtitle');
  private readonly teams = el('div', 'lobby-teams');
  private readonly levelRow = el('div', 'lobby-levelrow');
  /** Read-only: online you always play at your ladder 다마, so this shows it
   *  rather than offering a choice (the server ignores any client-set level). */
  private readonly levelVal = el('span', 'ui-chip ui-chip--accent lobby-levelval');
  private readonly levelHint = el('div', 'lobby-hint');
  private myLevel = 100;
  private readonly readyBtn = el('button', 'lobby-ready');
  private readonly itemsBtn = el('button', 'ui-btn ui-btn--ghost ui-btn--sm lobby-items');
  private readonly startBtn = el('button', 'lobby-start');
  private readonly startHint = el('div', 'lobby-hint');
  private storeEnabled = false; // server's 'itemStore' flag — see setStoreEnabled
  private myReady = false;

  constructor(root: HTMLElement, cb: LobbyCallbacks) {
    this.cb = cb;
    this.panel.id = 'lobby';
    this.buildBrowser();
    this.buildCreate();
    this.buildWait();
    this.panel.append(this.browser, this.create, this.wait);
    root.appendChild(this.panel);
    onLang(() => this.relabel());
    this.showBrowser();
    this.hide();
  }

  // ---- browser screen ----
  //
  // Structure, top to bottom: who you are → what you can join → how to make one.
  // The ladder shares the middle with the room list behind a tab rather than
  // stacking under it, and the account form is collapsed by default: playing
  // anonymously is the common path and shouldn't cost half the panel.
  private buildBrowser(): void {
    const head = el('div', 'lobby-head');
    const title = el('div', 'lobby-title');
    tText(title, 'lobby.title');
    const close = el('button', 'ui-btn ui-btn--ghost ui-btn--sm ui-btn--icon');
    close.textContent = '✕';
    close.title = t('lobby.toMenu');
    onLang(() => (close.title = t('lobby.toMenu')));
    close.onclick = () => this.cb.onClose();
    head.append(title, close);

    // identity plumbing (server-assigned nickname, reroll, avatar) stays live
    // even though the browser screen no longer shows an editable nickname row.
    this.nick.type = 'text';
    this.nick.maxLength = 24;
    this.nick.placeholder = t('lobby.nickPlaceholder');
    this.nick.onkeydown = (e) => e.stopPropagation();
    this.nick.onchange = () => this.cb.onNickname(this.nick.value.trim());
    this.reroll.textContent = '🎲';
    this.reroll.title = t('lobby.reroll');
    onLang(() => (this.reroll.title = t('lobby.reroll')));
    this.reroll.onclick = () => this.cb.onReroll();
    this.avatarThumb.title = t('avatar.edit');
    onLang(() => (this.avatarThumb.title = t('avatar.edit')));
    this.avatarThumb.onclick = () => this.cb.onEditAvatar();

    // tabs — rooms | ranking
    const tabs = el('div', 'lobby-tabs');
    ([['rooms', 'lobby.tabRooms'], ['rank', 'lobby.tabRanking']] as [Tab, string][]).forEach(
      ([id, key]) => {
        const b = el('button', 'lobby-tab');
        tText(b, key);
        b.onclick = () => this.showTab(id);
        this.tabBtns[id] = b;
        tabs.appendChild(b);
      }
    );

    this.roomsPane.append(this.roomList, this.status);
    this.rankPane.append(this.board);

    const createBtn = el('button', 'ui-btn ui-btn--primary ui-btn--block');
    tText(createBtn, 'lobby.createRoom');
    createBtn.onclick = () => this.showCreate();

    this.buildAuth();

    const foot = el('div', 'lobby-foot');
    foot.append(createBtn, this.authBox);

    this.browser.append(head, tabs, this.roomsPane, this.rankPane, foot);
    this.showTab('rooms');
  }

  private showTab(id: Tab): void {
    this.tab = id;
    this.roomsPane.style.display = id === 'rooms' ? 'flex' : 'none';
    this.rankPane.style.display = id === 'rank' ? 'flex' : 'none';
    (Object.keys(this.tabBtns) as Tab[]).forEach((k) => this.tabBtns[k].classList.toggle('on', k === id));
  }

  /** Account login / signup row (or a "signed in as …" line with logout). */
  private buildAuth(): void {
    this.authUser.type = 'text';
    this.authUser.maxLength = 20;
    this.authUser.placeholder = t('lobby.nickname');
    this.authPass.type = 'password';
    this.authPass.maxLength = 64;
    this.authPass.placeholder = t('lobby.password');
    this.authEmail.type = 'email';
    this.authEmail.maxLength = 80;
    this.authEmail.placeholder = t('lobby.email');
    for (const inp of [this.authUser, this.authPass, this.authEmail]) inp.onkeydown = (e) => e.stopPropagation();
    onLang(() => {
      this.authUser.placeholder = t('lobby.nickname');
      this.authPass.placeholder = t('lobby.password');
      this.authEmail.placeholder = t('lobby.email');
    });
    this.renderAuth();
  }

  private renderAuth(): void {
    this.authBox.replaceChildren();
    // Signed-in players get no form here — nothing to show, nothing to fold away.
    if (this.account && !this.isGuest) return;
    // Guest: anonymous play is the default path, so the form stays folded away
    // behind a disclosure until someone actually wants an account.
    const box = el('details', 'lobby-disclose');
    const summary = el('summary');
    tText(summary, 'lobby.signIn');
    const hint = el('div', 'lobby-authhint');
    tText(hint, 'lobby.guestHint');
    const row = el('div', 'lobby-authrow');
    const login = el('button', 'ui-btn ui-btn--sm');
    tText(login, 'lobby.login');
    login.onclick = () => this.submitAuth(false);
    const signup = el('button', 'ui-btn ui-btn--primary ui-btn--sm');
    tText(signup, 'lobby.signup');
    signup.onclick = () => this.submitAuth(true);
    this.authPass.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.submitAuth(false);
    };
    row.append(this.authUser, this.authPass, login, signup);
    box.append(summary, hint);
    // Social sign-in leads: it's one click and needs no password, which is the
    // whole reason the registered-account count has been zero.
    const social = this.renderSocial();
    if (social) box.append(social);
    box.append(row, this.authEmail, this.authMsg);
    // a failed attempt must not fold the form back up under the user; a main-menu
    // 로그인/가입 button lands here pre-expanded, same reasoning
    if (this.authMsg.textContent || this.authForceOpen) box.open = true;
    this.authBox.append(box);
  }

  /** Expand the guest auth form immediately — used when arriving from the main
   *  menu's 로그인/가입 buttons rather than discovering the disclosure by hand. */
  openAuth(): void {
    this.authForceOpen = true;
    this.renderAuth();
  }

  /**
   * Google / Kakao buttons. Returns null when the server reported no configured
   * providers, so we never show a button that dead-ends on a 503.
   */
  private renderSocial(): HTMLElement | null {
    if (this.providers.length === 0) return null;
    const box = el('div', 'lobby-social');
    for (const p of this.providers) {
      const btn = el('button', `ui-btn ui-btn--sm lobby-social-btn lobby-social-${p.id}`);
      btn.textContent = t(p.id === 'kakao' ? 'lobby.socialKakao' : 'lobby.socialGoogle');
      btn.onclick = () => {
        this.authMsg.textContent = '';
        this.cb.onSocialLogin(p.id);
      };
      box.append(btn);
    }
    const or = el('div', 'lobby-social-or');
    or.textContent = t('lobby.socialOr');
    box.append(or);
    return box;
  }

  /** Providers the server can complete a login with (from the `authed` reply). */
  setProviders(providers: AuthProvider[]): void {
    this.providers = providers;
    this.renderAuth();
  }

  /** Surface a social sign-in outcome in the same place as password errors. */
  setAuthMessage(msg: string): void {
    this.authMsg.textContent = msg;
    if (msg) this.renderAuth();
  }

  private submitAuth(isSignup: boolean): void {
    const u = this.authUser.value.trim();
    const p = this.authPass.value;
    if (!u || !p) {
      this.authMsg.textContent = t('lobby.authNeedBoth');
      return;
    }
    this.authMsg.textContent = '';
    if (isSignup) this.cb.onSignup(u, p, this.authEmail.value.trim());
    else this.cb.onLogin(u, p);
  }

  // ---- create screen ----
  private buildCreate(): void {
    const title = el('div', 'lobby-title');
    tText(title, 'lobby.createTitle');

    // game (variant) chooser
    const varLbl = el('div', 'lobby-lbl lobby-choicelbl');
    tText(varLbl, 'lobby.variant');
    for (const v of VARIANTS) {
      const b = el('button', 'lobby-choice');
      b.dataset.variant = v;
      b.textContent = t(`variant.${v}`);
      b.onclick = () => {
        this.chosenVariant = v;
        this.renderChoices();
      };
      this.varChoices.appendChild(b);
    }

    // format chooser
    const fmtLbl = el('div', 'lobby-lbl lobby-choicelbl');
    tText(fmtLbl, 'lobby.format');
    for (const f of FORMATS) {
      const b = el('button', 'lobby-choice');
      b.dataset.format = f;
      b.textContent = `${f} · ${FORMAT_INFO[f].players}P`;
      b.onclick = () => {
        this.chosenFormat = f;
        // switching format resets the finish rule to that format's default
        this.chosenFinish = defaultFinish(f);
        this.renderChoices();
        this.renderFinish();
      };
      this.fmtChoices.appendChild(b);
    }

    // finish rule (4-ball only): how many final 3-cushion caroms — and how many
    // of them must be bank shots — win the game. Chosen here at creation, then
    // locked in for the room (see net/protocol FinishRule).
    const finishLbl = el('label', 'lobby-lbl lobby-choicelbl');
    tText(finishLbl, 'lobby.finish');
    const caromLbl = el('div', 'lobby-sublbl');
    tText(caromLbl, 'lobby.finishCaroms');
    for (const n of [1, 2]) {
      const b = el('button', 'lobby-choice');
      b.dataset.caroms = String(n);
      b.textContent = `${n}`;
      b.onclick = () => this.pickFinish(n, this.chosenFinish.banks);
      this.caromChoices.appendChild(b);
    }
    const bankLbl = el('div', 'lobby-sublbl');
    tText(bankLbl, 'lobby.finishBanks');
    for (const n of [0, 1]) {
      const b = el('button', 'lobby-choice');
      b.dataset.banks = String(n);
      b.textContent = `${n}`;
      b.onclick = () => this.pickFinish(this.chosenFinish.caroms, n);
      this.bankChoices.appendChild(b);
    }
    this.finishBox.append(finishLbl, caromLbl, this.caromChoices, bankLbl, this.bankChoices);

    // opening style (4-ball only): preset layout vs cue-swing break
    const breakLbl = el('label', 'lobby-lbl lobby-choicelbl');
    tText(breakLbl, 'lobby.opening');
    const breakChoices = el('div', 'lobby-breakbtns');
    for (const style of ['preset', 'swing'] as BreakStyle[]) {
      const b = el('button', 'lobby-breakbtn');
      b.dataset.break = style;
      tText(b, style === 'preset' ? 'lobby.breakPreset' : 'lobby.breakSwing');
      b.onclick = () => {
        this.chosenBreakStyle = style;
        this.renderBreak();
      };
      this.breakBtns.push(b);
      breakChoices.appendChild(b);
    }
    this.breakRow.append(breakLbl, breakChoices);

    // 코인 stake: the loser pays this into a pot the winner(s) take.
    const stakeLbl = el('label', 'lobby-lbl lobby-choicelbl');
    tText(stakeLbl, 'lobby.stake');
    for (const amount of STAKES) {
      const o = el('option');
      o.value = String(amount);
      this.stakeSel.appendChild(o);
    }
    this.renderStakes();
    this.stakeSel.onchange = () => {
      this.chosenStake = Number(this.stakeSel.value);
    };
    this.stakeRow.append(stakeLbl, this.stakeSel);
    tText(this.stakeHint, 'lobby.stakeHint');

    // table colour picker — applies immediately, so it doubles as a live preview
    const themeLbl = el('label', 'lobby-lbl lobby-choicelbl');
    tText(themeLbl, 'lobby.tableColor');
    const swatches = el('div', 'lobby-swatches');
    for (const id of Object.keys(THEMES) as ThemeId[]) {
      const b = el('button', 'lobby-swatch');
      b.dataset.theme = id;
      b.style.background = '#' + THEMES[id].cloth.toString(16).padStart(6, '0');
      b.title = id;
      b.onclick = () => {
        this.chosenTheme = id;
        this.renderTheme();
        this.cb.onTheme(id);
      };
      this.themeBtns.push(b);
      swatches.appendChild(b);
    }
    this.themeRow.append(themeLbl, swatches);

    // background scene — also applies immediately, same reasoning as table colour
    const envLbl = el('label', 'lobby-lbl lobby-choicelbl');
    tText(envLbl, 'lobby.background');
    for (const id of ENV_IDS) {
      const o = el('option');
      o.value = id;
      o.textContent = t('env.' + id);
      this.envSel.appendChild(o);
    }
    this.envSel.onchange = () => {
      this.chosenEnv = this.envSel.value as EnvId;
      this.cb.onEnv(this.chosenEnv);
    };
    this.envRow.append(envLbl, this.envSel);

    const confirm = el('button', 'lobby-create');
    tText(confirm, 'lobby.create');
    // no typed name: the room is identified by its game type + the players in it.
    confirm.onclick = () =>
      this.cb.onCreate('', this.chosenFormat, this.chosenVariant, this.chosenFinish, this.chosenBreakStyle, this.chosenStake);

    const cancel = el('button', 'lobby-back');
    tText(cancel, 'lobby.cancel');
    cancel.onclick = () => this.showBrowser();

    this.create.append(
      title,
      varLbl,
      this.varChoices,
      fmtLbl,
      this.fmtChoices,
      this.sectionHeader('lobby.sectionOpening'),
      this.finishBox,
      this.breakRow,
      this.sectionHeader('lobby.sectionMatch'),
      this.stakeRow,
      this.stakeHint,
      this.sectionHeader('lobby.sectionTable'),
      this.themeRow,
      this.envRow,
      confirm,
      cancel
    );
  }

  private renderChoices(): void {
    for (const b of this.varChoices.children) b.classList.toggle('on', (b as HTMLElement).dataset.variant === this.chosenVariant);
    for (const b of this.fmtChoices.children) b.classList.toggle('on', (b as HTMLElement).dataset.format === this.chosenFormat);
    // finish rule + opening style only apply to 4-ball (straight-rail)
    const fourBall = this.chosenVariant === 'straight-rail';
    this.finishBox.style.display = fourBall ? '' : 'none';
    this.breakRow.style.display = fourBall ? '' : 'none';
  }

  /** Highlight the current finish-rule selection (caroms + banks). */
  private renderFinish(): void {
    for (const b of this.caromChoices.children) b.classList.toggle('on', Number((b as HTMLElement).dataset.caroms) === this.chosenFinish.caroms);
    for (const b of this.bankChoices.children) b.classList.toggle('on', Number((b as HTMLElement).dataset.banks) === this.chosenFinish.banks);
  }

  /** Picked the finish rule (4-ball) at creation time: clamp and highlight. */
  private pickFinish(caroms: number, banks: number): void {
    this.chosenFinish = { caroms, banks: Math.min(banks, caroms) };
    this.renderFinish();
  }

  /** Highlight the current opening-style selection. */
  private renderBreak(): void {
    for (const b of this.breakBtns) b.classList.toggle('on', b.dataset.break === this.chosenBreakStyle);
  }

  /**
   * Label the stake picker and grey out the amounts the host can't cover — the
   * host pays their own bet too, so picking one of those would only deadlock
   * the room at the start gate (the server refuses it for the same reason).
   */
  private renderStakes(): void {
    for (const o of Array.from(this.stakeSel.options)) {
      const amount = Number(o.value);
      const short = amount > this.coins;
      o.disabled = short;
      o.textContent =
        amount === 0
          ? t('lobby.stakeOff')
          : t('lobby.stakeAmount', { n: String(amount) }) + (short ? ` — ${t('lobby.stakeTooRich', { have: String(this.coins) })}` : '');
    }
    // a balance that just dropped below the pick falls back to "no stake"
    if (this.chosenStake > this.coins) this.chosenStake = 0;
    this.stakeSel.value = String(this.chosenStake);
  }

  /** Keep the 코인 balance current: it gates the stake picker and the room list. */
  setCoins(coins: number): void {
    if (coins === this.coins) return;
    this.coins = coins;
    this.renderStakes();
    this.setRooms(this.rooms);
  }

  /** Keep this account's ladder tier current: staked rooms gate joining (and
   *  are badged in the list) by how far the host's tier is from this one. */
  setMyTier(tier: number | null): void {
    if (tier === this.myTier) return;
    this.myTier = tier;
    this.setRooms(this.rooms);
  }

  // ---- waiting-room screen ----
  private buildWait(): void {
    this.readyBtn.onclick = () => {
      this.myReady = !this.myReady;
      this.cb.onReady(this.myReady);
    };

    // Equip purchased items (cue-ball colour, etc.) before the match — a
    // seated player's own thing, not room config.
    tText(this.itemsBtn, 'lobby.items');
    this.itemsBtn.onclick = () => this.cb.onOpenItems();

    // your 다마 (Korean handicap) — shown, not chosen: online games are always
    // played at your ladder tier so the handicap can't be used to dodge it
    const lvlHead = el('div', 'lobby-levelhead');
    const lvlLbl = el('label', 'lobby-lbl');
    tText(lvlLbl, 'lobby.yourLevel');
    lvlHead.append(lvlLbl, this.levelVal);
    tText(this.levelHint, 'lobby.levelFixed');
    this.levelRow.append(lvlHead, this.levelHint);

    tText(this.startBtn, 'lobby.start');
    this.startBtn.onclick = () => this.cb.onStart();

    const leaveBtn = el('button', 'ui-btn ui-btn--danger ui-btn--sm');
    tText(leaveBtn, 'lobby.leave');
    leaveBtn.onclick = () => this.cb.onLeave();

    // Header: what this room is, and the way out.
    const head = el('div', 'lobby-head');
    head.append(this.waitTitle, leaveBtn);

    // Room config is all chosen at creation now, so the waiting room is just
    // the people in it plus your own commitment (level + ready).
    const playersLbl = el('div', 'ui-label');
    tText(playersLbl, 'lobby.players');

    const foot = el('div', 'lobby-foot');
    foot.append(this.levelRow, this.itemsBtn, this.readyBtn, this.startBtn, this.startHint);

    this.wait.append(head, playersLbl, this.teams, foot);
  }

  /** A small labeled divider that breaks the creation form into named groups. */
  private sectionHeader(key: string): HTMLElement {
    const h = el('div', 'lobby-sectionhdr');
    tText(h, key);
    return h;
  }

  private relabel(): void {
    this.nick.placeholder = t('lobby.nickPlaceholder');
    for (const b of this.varChoices.children) (b as HTMLElement).textContent = t(`variant.${(b as HTMLElement).dataset.variant as VariantId}`);
    [...this.envSel.options].forEach((o) => (o.textContent = t('env.' + o.value)));
    this.renderStakes();
    this.setRooms(this.rooms);
    this.renderReadyBtn();
  }

  // ---- public API (driven by App) ----
  show(): void {
    this.panel.style.display = 'flex';
  }

  hide(): void {
    this.panel.style.display = 'none';
  }

  private only(screen: HTMLElement): void {
    for (const s of [this.browser, this.create, this.wait]) s.style.display = s === screen ? 'flex' : 'none';
  }

  showBrowser(): void {
    this.only(this.browser);
    this.panel.classList.remove('lobby-docked');
    this.show();
  }

  showCreate(): void {
    this.only(this.create);
    this.renderChoices();
    this.renderFinish();
    this.renderBreak();
    this.panel.classList.remove('lobby-docked');
    this.show();
  }

  setNickname(name: string): void {
    // Signed-in players play under their account name, not a locally-typed one;
    // ignore updates to the (disabled) field until they log out.
    if (this.account && !this.isGuest) return;
    this.nick.value = name;
  }

  /** Repaint the profile avatar thumbnail next to the nickname. */
  setAvatar(cfg: AvatarConfig): void {
    paintAvatar(this.avatarThumb, cfg);
  }

  /** Reflect the current session (account = registered nickname, null for a guest). */
  setAuth(account: string | null, guest = false): void {
    this.account = account;
    this.isGuest = guest;
    if (account && !guest) {
      this.authPass.value = '';
      this.authEmail.value = '';
      this.authMsg.textContent = '';
      // Online play uses the account name once signed in; the nickname field
      // becomes read-only so it can't drift from the identity actually in use.
      this.nick.value = account;
    }
    this.nick.disabled = !!account && !guest;
    this.reroll.disabled = !!account && !guest;
    this.renderAuth();
  }

  setAuthError(message: string): void {
    this.authMsg.textContent = message; // set first: renderAuth keeps the form open when there's an error
    this.renderAuth();
  }

  /** Tell the board which player id is "me" (for highlighting). */
  setMyId(id: string | null): void {
    this.accountId = id;
  }

  /** Show/hide the pre-match items button per the server's 'itemStore' flag. */
  setStoreEnabled(enabled: boolean): void {
    this.storeEnabled = enabled;
    if (!enabled) this.itemsBtn.style.display = 'none'; // the next room render re-shows it for players
  }

  /** Render the top-players leaderboard (rating-sorted, bots flagged). */
  setLeaderboard(entries: LeaderboardEntry[]): void {
    this.board.replaceChildren();
    if (entries.length === 0) {
      const empty = el('div', 'lobby-empty');
      tText(empty, 'lobby.noRanks');
      this.board.appendChild(empty);
      return;
    }
    for (const e of entries) {
      const row = el('div', `lobby-boardrow${this.accountId && e.id === this.accountId ? ' me' : ''}`);
      const rank = el('span', 'lobby-boardrank');
      rank.textContent = `${e.rank}`;
      const nm = el('span', 'lobby-boardnm');
      nm.textContent = e.name;
      const tier = el('span', 'lobby-boardtier');
      tier.textContent = tierLabel(e.tier);
      const elo = el('span', 'lobby-boardelo');
      elo.textContent = `${e.rating}`;
      elo.title = 'Elo';
      row.append(rank, nm, tier, elo);
      this.board.appendChild(row);
    }
  }

  setStatus(text: string): void {
    this.status.textContent = text;
  }

  /** Render the browsable list of open rooms. */
  setRooms(rooms: RoomInfo[]): void {
    this.rooms = rooms;
    this.roomList.replaceChildren();
    if (rooms.length === 0) {
      const empty = el('div', 'lobby-empty');
      tText(empty, 'lobby.noRooms');
      this.roomList.appendChild(empty);
      return;
    }
    for (const r of rooms) {
      const row = el('div', 'lobby-roomrow');
      const info = el('div', 'lobby-roominfo');
      // room "name" = its game type; below it, the players currently in it
      const nm = el('span', 'lobby-roomnm');
      const variantIcon = r.variant === 'three-cushion' ? '🎱' : '🔴';
      nm.textContent = `${variantIcon} ${t(`variant.${r.variant}`)} · ${r.format}`;
      const who = el('span', 'lobby-roomwho');
      who.textContent = r.playerNames.length ? r.playerNames.join(', ') : t('lobby.empty');
      const meta = el('span', 'lobby-roommeta');
      const statusTxt = r.status === 'playing' ? t('lobby.playing') : t('lobby.waitingShort');
      const stake = r.stake || 0;
      const rankGated = stake > 0 && r.hostTier != null;
      meta.textContent =
        `${r.players}/${r.capacity} · ${statusTxt}` +
        (stake > 0 ? ` · ${t('lobby.stakeBadge', { n: String(stake) })}` : '') +
        (rankGated ? ` · ${t('lobby.rankGapBadge', { n: String(MAX_STAKE_RANK_GAP) })}` : '');
      info.append(nm, who, meta);
      const joinBtn = el('button', 'lobby-joinbtn');
      tText(joinBtn, r.status === 'playing' ? 'lobby.watch' : 'lobby.join');
      joinBtn.onclick = () => this.cb.onJoin(r.code);
      // only a seat costs the stake (and the rank gate) — watching a game in
      // progress is free either way
      if (r.status === 'waiting' && stake > this.coins) {
        joinBtn.disabled = true;
        // on the row, not the button: a disabled button swallows no pointer
        // events, so its own title would never surface on hover
        row.title = t('lobby.joinStakeShort', { n: String(stake), have: String(this.coins) });
      } else if (r.status === 'waiting' && rankGated && this.myTier != null && tierStepsApart(this.myTier, r.hostTier!) > MAX_STAKE_RANK_GAP) {
        joinBtn.disabled = true;
        row.title = t('lobby.joinRankGap', { n: String(MAX_STAKE_RANK_GAP), tier: tierLabel(r.hostTier!) });
      }
      row.append(info, joinBtn);
      this.roomList.appendChild(row);
    }
  }

  /** Render the waiting room from a server snapshot. */
  setLobby(state: LobbyState): void {
    this.only(this.wait);
    // dock to the side so the practice table stays visible + clickable
    this.panel.classList.add('lobby-docked');
    this.show();

    const me = state.members.find((m) => m.id === state.youId);
    const isHost = !!me && me.host;
    this.myReady = !!me && me.ready;

    this.waitTitle.textContent = `${t(`variant.${state.variant}`)} · ${state.format} · ${t('lobby.rankedGame')}`;

    // team columns
    this.teams.replaceChildren();
    const teamCount = FORMAT_INFO[state.format].teams;
    for (let ti = 0; ti < teamCount; ti++) {
      const col = el('div', 'lobby-team');
      const h = el('div', 'lobby-teamhdr');
      h.textContent = t('lobby.team', { n: String(ti + 1) });
      col.appendChild(h);
      for (const m of state.members.filter((x) => x.team === ti)) {
        col.appendChild(this.memberRow(m, state.youId, isHost));
      }
      this.teams.appendChild(col);
    }
    const specs = state.members.filter((m) => m.role === 'spectator');
    if (specs.length) {
      const col = el('div', 'lobby-team lobby-specs');
      const h = el('div', 'lobby-teamhdr');
      h.textContent = t('lobby.spectators');
      col.appendChild(h);
      for (const m of specs) col.appendChild(this.memberRow(m, state.youId));
      this.teams.appendChild(col);
    }

    const isPlayer = !!me && me.role === 'player';
    this.levelRow.style.display = isPlayer ? '' : 'none';
    if (me) {
      this.myLevel = me.level;
      this.levelVal.textContent = tierLabel(me.level);
    }

    this.readyBtn.style.display = isPlayer ? '' : 'none';
    this.itemsBtn.style.display = isPlayer && this.storeEnabled ? '' : 'none';
    this.renderReadyBtn();

    const players = state.members.filter((m) => m.role === 'player');
    const seated = players.length;
    const cap = FORMAT_INFO[state.format].players;
    const allReady = seated === cap && players.every((m) => m.ready);
    const allStaked = players.every((m) => m.staked !== false);

    // only the host gets a Start button; it enables once everyone is ready AND
    // (if a stake is set) can actually cover it
    this.startBtn.style.display = isHost ? '' : 'none';
    this.startBtn.disabled = !allReady || !allStaked;
    if (seated < cap) this.startHint.textContent = t('lobby.needPlayers', { n: String(cap - seated) });
    else if (!allStaked) this.startHint.textContent = t('lobby.stakeShort', { n: String(state.stake) });
    else if (!allReady) this.startHint.textContent = t('lobby.waitReady');
    else this.startHint.textContent = isHost ? t('lobby.canStart') : t('lobby.waitHost');
  }

  private memberRow(
    m: { id: string; name: string; ready: boolean; host: boolean; role: string; level: number; avatar?: string; staked?: boolean },
    youId: string,
    viewerIsHost = false
  ): HTMLElement {
    const row = el('div', `lobby-member${m.id === youId ? ' me' : ''}`);
    const dot = el('span', `lobby-readydot${m.ready ? ' on' : ''}`);
    const pic = el('span', 'lobby-memberpic');
    if (AVATAR_ENABLED && m.role === 'player' && m.avatar) paintAvatar(pic, parseAvatar(m.avatar));
    const nm = el('span', 'lobby-membernm');
    nm.textContent = (m.host ? '★ ' : '') + m.name + (m.id === youId ? ` (${t('lobby.you')})` : '');
    row.append(dot, pic, nm);
    if (m.role === 'player') {
      const lv = el('span', 'lobby-memberlv');
      lv.textContent = String(m.level);
      row.append(lv);
      if (m.staked === false) {
        const warn = el('span', 'lobby-stakewarn');
        warn.textContent = '\u{1FA99}'; // coin emoji — "can't cover the stake"
        warn.title = t('lobby.stakeWarn');
        row.append(warn);
      }
    }
    // host-only: a kick button on everyone but themselves
    if (viewerIsHost && m.id !== youId) {
      const kick = el('button', 'lobby-kick interactive');
      kick.textContent = '✕';
      kick.title = t('lobby.kick');
      onLang(() => (kick.title = t('lobby.kick')));
      kick.onclick = () => this.cb.onKick(m.id);
      row.append(kick);
    }
    return row;
  }

  /** Reflect the local player's current table colour in the swatch row. */
  setTheme(id: ThemeId): void {
    this.chosenTheme = id;
    this.renderTheme();
  }

  private renderTheme(): void {
    for (const b of this.themeBtns) b.classList.toggle('on', b.dataset.theme === this.chosenTheme);
  }

  /** Reflect the room's background scene (admin-set, synced) in the picker. */
  setEnv(id: EnvId): void {
    this.chosenEnv = id;
    this.envSel.value = id;
  }

  private renderReadyBtn(): void {
    this.readyBtn.textContent = t(this.myReady ? 'lobby.unready' : 'lobby.ready');
    this.readyBtn.classList.toggle('on', this.myReady);
  }
}
